/**
 * Expected Delivery Interfaces
 *
 * Dieses File definiert die Base-Interfaces, die auch von der LagerApp importiert werden.
 * Base-Interfaces entsprechen den Backend base_hash Methoden.
 */

// ============================================================================
// Konstanten (werden von LagerApp importiert)
// ============================================================================

/**
 * Lieferstatus-Konstanten
 */
export const DELIVERY_STATUS = {
  OPEN: 'open',                         // Normale erwartete Lieferung, Einbuchung möglich
  PARTIAL: 'partial',                   // Teilweise eingebucht
  COMPLETED: 'completed',               // Vollständig eingebucht
  CANCELLED: 'cancelled',               // Storniert
  UNEXPECTED: 'unexpected',             // Unerwartete Lieferung, wartet auf Büro-Freigabe
  PENDING_CONFIRMATION: 'pending_confirmation' // Büro hat freigegeben, Lager muss bestätigen
} as const;

export type DeliveryStatusType = typeof DELIVERY_STATUS[keyof typeof DELIVERY_STATUS];

/**
 * Vordefinierte Handlungsanweisungen
 */
export const HANDLING_INSTRUCTIONS = {
  STOCK_IN: 'STOCK_IN',   // Einlagerung ins Hauptlager
  FORWARD: 'FORWARD'      // Weiterversand an Techniker/Kunden
} as const;

export type HandlingInstructionType = typeof HANDLING_INSTRUCTIONS[keyof typeof HANDLING_INSTRUCTIONS];

// ============================================================================
// Shared Interfaces (werden von LagerApp importiert)
// ============================================================================

/**
 * Gescannter Lieferschein-PDF
 */
export interface DeliveryNotePdf {
  filename: string;
  created_at: string;
  size: number;
}

/**
 * Owner-Typ für Artikel
 */
export type OwnerType = 'BENTOMAX' | 'CUSTOMER';

/**
 * Verpackungstyp
 */
export type PackagingType = 'PALLET' | 'PACKAGE';

// ============================================================================
// Base Interfaces (entsprechen Backend base_hash)
// ============================================================================

/**
 * ExpectedDelivery Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryBase {
  id: number;
  status: DeliveryStatusType;
  supplier_name: string;
  order_nr: string | null;
  project_id: number | null;
  project_name: string | null;
  customer_id: number | null;
  customer_name: string | null;
  packaging_type: PackagingType | null;
  handling_instruction: string;
  expected_from: string | null;
  expected_until: string | null;
  total_ordered: number;
  total_received: number;
  total_open: number;
  created_at: string;
  delivery_note_pdfs: DeliveryNotePdf[];
  source_warehouse_id: number | null;
  source_warehouse_label: string | null;
}

/**
 * ExpectedDeliveryItem Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryItemBase {
  id: number;
  article_template_id: number | null;
  article_template_description: string | null;
  article_template_part_nr: string | null;
  article_template_sn: boolean | null;
  custom_description: string | null;
  display_description: string | null;
  serial_numbers_raw: string[] | null;
  quantity_ordered: number;
  quantity_received: number;
  quantity_open: number;
  owner: OwnerType;
  pending_storage_bin_id: number | null;
  pending_storage_bin_label: string | null;
}

/**
 * ExpectedDeliveryReceipt Base - Gemeinsame Felder aus base_hash
 */
export interface ExpectedDeliveryReceiptBase {
  id: number;
  quantity: number;
  received_at: string;
  received_by_name: string;
  storage_bin_label: string;
  pending_confirmation: boolean;
  article_serial_numbers: string[];
}

// ============================================================================
// Büro-Frontend spezifische Interfaces (erweitern Base)
// ============================================================================

/**
 * ExpectedDelivery für Büro-Frontend (list_hash + detail_hash)
 */
export interface ExpectedDelivery extends ExpectedDeliveryBase {
  // list_hash Felder
  progress_percentage: number;
  items_count: number;
  created_by_name: string;
  has_quick_captured?: boolean;
  // detail_hash Felder (optional)
  external_delivery_note_number?: string | null;
  delivery_note_id?: number | null;
  delivery_note_number?: string | null;
  ticket_id?: number | null;
  ticket_order_nr?: string | null;
  notes?: string | null;
  internal_notification_emails?: string | null;
  external_notification_emails?: string | null;
  order_date?: string | null;
  updated_at?: string;
  created_by_id?: number;
  items?: ExpectedDeliveryItem[];
  quick_captured_deliveries?: { id: number; supplier_name: string | null; pdf_exists: boolean }[];
  /**
   * Offene Klärfälle über die ganze Lieferung. Erklärt zugleich, warum eine
   * Lieferung trotz erfüllter Mengen nicht auf "vollständig" steht.
   */
  open_clarifications_count?: number;
}

/**
 * ExpectedDeliveryItem für Büro-Frontend (detail_hash)
 */
export interface ExpectedDeliveryItem extends ExpectedDeliveryItemBase {
  article_template_vendor?: string | null;
  fully_received?: boolean;
  created_at?: string;
  _destroy?: boolean; // Für nested attributes
}

/**
 * ExpectedDeliveryReceipt für Büro-Frontend (detail_hash)
 */
export interface ExpectedDeliveryReceipt extends ExpectedDeliveryReceiptBase {
  received_by_id: number;
  storage_bin_id: number;
  warehouse_name: string;
  external_delivery_note_number_scan: string | null;
  article_ids: number[];
  created_at: string;
  article_template_description: string;
  /** Positionen dieser Buchung, über die das Büro noch entscheiden muss. */
  open_clarifications_count: number;
  /** Wie viele Klärfälle es insgesamt gab — auch die bereits entschiedenen. */
  clarifications_count: number;
}

// ============================================================================
// Klärfälle
//
// Entstehen, wenn bei einer internen Lieferung nicht alles aus dem Quelllager
// umgebucht werden konnte. Früher wurden die fehlenden Artikel still neu
// angelegt; jetzt entscheidet das Büro darüber.
// ============================================================================

export type ClarificationAction = 'booked_back' | 'transferred' | 'created' | 'ignored';

/** Eine einzelne Entscheidung des Büros — Teilmengen sind möglich. */
export interface ClarificationActionEntry {
  id: number;
  action: ClarificationAction;
  quantity: number;
  article_status: string | null;
  source_article_id: number | null;
  source_delivery_note_id: number | null;
  note: string | null;
  decided_by_id: number;
  decided_by_name: string | null;
  created_at: string;
}

/**
 * Ein Artikel, mit dem sich der Klärfall auflösen lässt — entweder weil er im
 * Versand hängt, oder weil er die gesuchte Seriennummer trägt. Beide Quellen
 * landen in einer Liste; `delivery_note_id` sagt, welche es war.
 */
export interface ClarificationCandidate {
  id: number;
  serial_number: string | null;
  bad_part: boolean;
  location: string;
  storage_bin_label: string | null;
  warehouse_label: string | null;
  consumed_reason: string | null;
  created_at: string;
  /** Der quittierte Eingang ins Quell-Lager, über den der Artikel dort landete. */
  delivery_note_id: number | null;
  delivery_note_number: string | null;
  delivery_note_date: string | null;
  /**
   * Der Lieferschein, auf dem der Artikel laut System gerade unterwegs ist.
   * Bei einer Sperre ist es der sperrende, sonst reine Information.
   */
  transit_note: { id: number; number: string | null; recipient: string } | null;
  /** Sichtbar, aber nicht wählbar — mit Begründung. */
  selectable: boolean;
  blocked_reason: string | null;
}

/** Ein Lagerplatz zur Auswahl im Defektteil-Block. */
export interface ClarificationBinOption {
  id: number;
  label: string;
}

/**
 * Auswahl für den Defektteil-Lagerplatz, je Buchung. Bewusst alle Lagerplätze
 * des Ziel-Lagers: es gibt kein Merkmal, an dem sich Defektteil-Plätze erkennen
 * ließen, und das Büro darf Defektteile auch einem Kunden zuordnen.
 */
export interface ClarificationBadPartBins {
  storage_bins: ClarificationBinOption[];
  suggested_id: number | null;
}

export interface ExpectedDeliveryClarification {
  id: number;
  expected_delivery_items_receipt_id: number;
  /** Nur bei Vorlagen mit Seriennummernpflicht gesetzt; dann ist quantity 1. */
  serial_number: string | null;
  quantity: number;
  quantity_resolved: number;
  quantity_open: number;
  status: 'open' | 'resolved';
  created_at: string;
  expected_delivery_item_id: number;
  article_template_id: number | null;
  article_template_description: string | null;
  article_template_part_nr: string | null;
  article_template_sn: boolean | null;
  quantity_ordered: number;
  quantity_received: number;
  actions: ClarificationActionEntry[];
  candidates: ClarificationCandidate[];
  /** Mehrere aktive Einträge zur Nummer — das System wählt bewusst nicht selbst. */
  ambiguous_serial_number: boolean;
}

export interface ResolveClarificationParams {
  action_type: ClarificationAction;
  quantity?: number;
  article_ids?: number[];
  article_status?: string;
  delivery_note_id?: number;
  /** Pflicht, sobald ein Defektteil dabei ist — sonst weist das Backend ab. */
  bad_part_storage_bin_id?: number;
  note?: string;
}

// ============================================================================
// Parameter Interfaces (Büro-spezifisch)
// ============================================================================

export interface ExpectedDeliveryCreateParams {
  supplier_name: string;
  order_nr?: string;
  external_delivery_note_number?: string;
  delivery_note_id?: number;
  ticket_id?: number;
  project_id?: number;
  customer_id?: number;
  packaging_type?: PackagingType;
  handling_instruction: string;
  notes?: string;
  internal_notification_emails?: string;
  external_notification_emails?: string;
  order_date?: string;
  expected_from?: string;
  expected_until?: string;
  source_warehouse_id?: number | null;
  expected_delivery_items_attributes?: Partial<ExpectedDeliveryItem>[];
}

/**
 * Parameter für die Freigabe mit Receipts
 */
export interface ReleaseWithReceiptsParams {
  handling_instruction: string;
  order_nr?: string;
  ticket_id?: number | null;
  project_id?: number | null;
  customer_id?: number | null;
  expected_from?: string | null;
  expected_until?: string | null;
  internal_notification_emails?: string;
  external_notification_emails?: string;
  packaging_type?: PackagingType | null;
  notes?: string;
  delivery_note_id?: number | null;
  source_warehouse_id?: number | null;
  items: ReleaseItemParams[];
}

export interface ReleaseItemParams {
  id: number;
  article_template_id: number;
  quantity_ordered?: number;
  owner?: OwnerType;
  storage_bin_id: number;
}
